import { useCallback, useEffect, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { Alert } from "@/components/ui/Alert";
import { Spinner } from "@/components/ui/Spinner";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { ApiError, api } from "@/lib/apiClient";
import {
  employeeDisplayFromRecord,
  employeeLoginFromRecord,
  type HeroEmployeeRecord,
} from "@/lib/employees";
import {
  listPersonnelCostCategories,
  listPersonnelCostEmployeeSettings,
  listPersonnelCostEntries,
  type PersonnelCostCategory,
  type PersonnelCostEmployeeSettings,
  type PersonnelCostEntry,
} from "@/lib/personnelCosts";
import { usePersonalNavigation } from "@/lib/personalNav";
import { EmployeeCostOverview } from "@/pages/personal/personalkosten/EmployeeCostOverview";
import { CostEntriesPanel } from "@/pages/personal/personalkosten/CostEntriesPanel";
import { HourlyRateCalculator } from "@/pages/personal/personalkosten/HourlyRateCalculator";

export type PersonalkostenTab = "overview" | "costs" | "calculator";

export interface EmployeeOption {
  login: string;
  display: string;
}

export function PersonalkostenPage() {
  const { backLink: personalBack } = usePersonalNavigation();
  const [activeTab, setActiveTab] = useState<PersonalkostenTab>("overview");
  const [selectedLogin, setSelectedLogin] = useState<string | null>(null);

  const [employees, setEmployees] = useState<EmployeeOption[]>([]);
  const [categories, setCategories] = useState<PersonnelCostCategory[]>([]);
  const [entries, setEntries] = useState<PersonnelCostEntry[]>([]);
  const [settings, setSettings] = useState<PersonnelCostEmployeeSettings[]>([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (options?: { silent?: boolean }) => {
    if (!options?.silent) setLoading(true);
    setError(null);
    try {
      const [heroList, categoryList, entryList, settingsList] = await Promise.all([
        api.get<HeroEmployeeRecord[]>("/hero/employees"),
        listPersonnelCostCategories(),
        listPersonnelCostEntries(),
        listPersonnelCostEmployeeSettings(),
      ]);

      const employeeOptions: EmployeeOption[] = (Array.isArray(heroList) ? heroList : [])
        .filter((emp) => (emp.status ?? "active").toLowerCase() !== "inactive")
        .map((emp) => {
          const login = employeeLoginFromRecord(emp);
          if (!login) return null;
          return {
            login,
            display: employeeDisplayFromRecord(emp),
          };
        })
        .filter((row): row is EmployeeOption => row != null)
        .sort((a, b) => a.display.localeCompare(b.display, "de"));

      setEmployees(employeeOptions);
      setCategories(categoryList);
      setEntries(entryList);
      setSettings(settingsList);

      setSelectedLogin((current) => current ?? employeeOptions[0]?.login ?? null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Personalkosten konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  function openCalculator(login: string) {
    setSelectedLogin(login);
    setActiveTab("calculator");
  }

  return (
    <AppShell
      title="Personalkosten"
      subtitle="Laufende Kosten erfassen und voll belastete Stundenlöhne kalkulieren."
      back={personalBack ?? { to: "/", label: "Home" }}
    >
      <div className="mb-5">
        <SegmentedControl
          value={activeTab}
          onChange={setActiveTab}
          name="Personalkosten-Bereich"
          options={[
            { value: "overview", label: "Übersicht" },
            { value: "costs", label: "Kosten erfassen" },
            { value: "calculator", label: "Stundenlohn-Rechner" },
          ]}
        />
      </div>

      {error ? <Alert variant="error" className="mb-4">{error}</Alert> : null}

      {loading ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : activeTab === "overview" ? (
        <EmployeeCostOverview
          employees={employees}
          categories={categories}
          entries={entries}
          settings={settings}
          onSelectEmployee={openCalculator}
          onSettingsSaved={(saved) => {
            setSettings((prev) => {
              const idx = prev.findIndex((s) => s.user_login === saved.user_login);
              if (idx < 0) return [...prev, saved];
              const next = [...prev];
              next[idx] = saved;
              return next;
            });
          }}
        />
      ) : activeTab === "costs" ? (
        <CostEntriesPanel
          employees={employees}
          categories={categories}
          entries={entries}
          selectedLogin={selectedLogin}
          onSelectedLoginChange={setSelectedLogin}
          onRefresh={() => void load({ silent: true })}
        />
      ) : (
        <HourlyRateCalculator
          employees={employees}
          categories={categories}
          entries={entries}
          settings={settings}
          selectedLogin={selectedLogin}
          onSelectedLoginChange={setSelectedLogin}
          onSettingsSaved={(saved) => {
            setSettings((prev) => {
              const idx = prev.findIndex((s) => s.user_login === saved.user_login);
              if (idx < 0) return [...prev, saved];
              const next = [...prev];
              next[idx] = saved;
              return next;
            });
          }}
        />
      )}
    </AppShell>
  );
}
